import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { updateEPCRating } from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import SingleSelect from '../../formElements/SingleSelect';
import FormActions from '../../ui/formActions/FormActions';

import { validate } from './validateEnvironment';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const message = 'Participant EPC rating has been updated';

const EditEPCRating = ({ row, isEditDisabled, onClose }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        epcRatingId: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRowState, setInitialRowState] = useState({});
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const epcRatingsDetails = useSelector(
        (state) => state.entities.directusService.epcRatingsDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!currentParticipant?.id || !row) return;
        const data = {
            id: row.id,
            participantId: row.participantId,
            epcRatingId: row.epcRatingId
        };
        setNewEntry(data);
        setInitialRowState(data);
    }, [currentParticipant, row]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === message || errorMessage) clearForm();
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setInitialRowState(initialState);
        setIsButtonDisabled(false);
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onFormExit = () => {
        clearForm();
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, 'edit', initialRowState);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateEPCRating(newEntry, message));
        onFormExit();
    };

    // RENDER
    return (
        <form onSubmit={onSubmit} data-testid="form_start_edit_epc_rating">
            <h2 className={app.sectionHeading}>Edit EPC Rating</h2>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <SingleSelect
                        id="epcRatingId"
                        label="EPC Rating"
                        placeholder="Select EPC rating..."
                        disabled={isEditDisabled}
                        mandatory={true}
                        menuItems={epcRatingsDetails || []}
                        selectedId={newEntry.epcRatingId || ''}
                        selected={getObjFromId(epcRatingsDetails, newEntry.epcRatingId)}
                        error={errors.epcRatingId}
                        onChange={(chosenId) => onUpdate('epcRatingId', chosenId)}
                    />
                </div>
                <div className={form.formColumn}></div>
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onFormExit}
                btnText="Update EPC Rating"
                disabled={isEditDisabled || isButtonDisabled}
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

EditEPCRating.propTypes = {
    row: PropTypes.object,
    isEditDisabled: PropTypes.bool,
    onClose: PropTypes.func
};

export default EditEPCRating;
